import { useStore } from '@nanostores/react';
import { computed } from 'nanostores';
import { memo, useEffect } from 'react';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import { renderLogger } from '~/utils/logger';
import { Preview } from './Preview';
import { PanelTabBar } from './PanelTabBar';
import { ToolLauncher } from './ToolLauncher';
import { DeploymentPanel } from './DeploymentPanel';
import { FormsPanel } from './tools/FormsPanel';
import { IntegrationsPanel } from './tools/IntegrationsPanel';
import { AssetsPanel } from './tools/AssetsPanel';
import { AnalyticsPanel } from './tools/AnalyticsPanel';
import { DomainsPanel } from './tools/DomainsPanel';
import { PaymentsPanel } from './tools/payments/PaymentsPanel';
import { ShopPanel } from './tools/shop/ShopPanel';
import { FunnelsPanel } from './tools/funnels/FunnelsPanel';
import { MarketingPanel } from './tools/marketing/MarketingPanel';
import { searchConsoleReturn } from './tools/marketing/marketing-model';
import { ReviewsPanel } from './tools/reviews/ReviewsPanel';
import { ContentPanel } from './tools/content/ContentPanel';
import { AutomationsPanel } from './tools/automations/AutomationsPanel';
import { MarketplacePanel } from './tools/marketplace/MarketplacePanel';
import { SectionsPanel } from './tools/sections/SectionsPanel';
import { marketplaceReturn } from './tools/marketplace/marketplace-model';
import { $marketplaceIntent } from '~/lib/stores/marketplaceIntent';
import { CodeViewerPanel } from './CodeViewerPanel';
import { CodeLockedPanel } from './CodeLockedPanel';
import { usePlanAccess } from '~/lib/planGate';
import { SubAgentPanel } from './tools/SubAgentPanel';
import useViewport from '~/lib/hooks';
import { chatStore } from '~/lib/stores/chat';
import { ConnectionTimeoutBanner } from './ConnectionTimeoutBanner';

interface WorkspaceProps {
  chatStarted?: boolean;
  isStreaming?: boolean;
  metadata?: {
    gitUrl?: string;
  };
  updateChatMestaData?: (metadata: any) => void;
}

export const Workbench = memo(
  ({
    chatStarted,
    isStreaming: _isStreaming,
    metadata: _metadata,
    updateChatMestaData: _updateChatMestaData,
  }: WorkspaceProps) => {
    renderLogger.trace('Workbench');

    const hasPreview = useStore(computed(workbenchStore.previews, (previews) => previews.length > 0));
    const showWorkbench = useStore(workbenchStore.showWorkbench);
    const { showChat } = useStore(chatStore);
    const canHideChat = showWorkbench || !showChat;
    const isSmallViewport = useViewport(1024);
    const tabs = useStore(workbenchStore.panelTabs);
    // the Code tab is paid-only; the server also refuses free users the file tree
    const { isFree: codeLocked } = usePlanAccess();
    const activeTabId = useStore(workbenchStore.activeTabId);

    const activeTab = tabs.find((t) => t.id === activeTabId) || tabs[0];

    // Auto-show workbench when a preview appears or the chat starts — unless the user
    // has explicitly closed it (then they're in full-width chat and reopen via the
    // Preview/Publish buttons), or the viewport is narrow, where the panel is a
    // full-screen overlay that would hide the build.
    useEffect(() => {
      if (hasPreview) {
        workbenchStore.autoShowWorkbench();
      }
    }, [hasPreview]);

    useEffect(() => {
      if (chatStarted) {
        workbenchStore.autoShowWorkbench();
      }
    }, [chatStarted]);

    // Google Search Console's OAuth sends the owner back with ?searchConsole=…;
    // reopen the Marketing panel so it can show the outcome.
    useEffect(() => {
      if (chatStarted && searchConsoleReturn(window.location.search)) {
        workbenchStore.openTool('marketing');
      }
    }, [chatStarted]);

    // Stripe Checkout for a paid marketplace install sends the owner back with
    // ?marketplace=…; reopen the Marketplace panel so it can show the outcome.
    // A storefront deep link (?marketplace=<slug>, ?expert=…) opens it too.
    const marketplaceIntent = useStore($marketplaceIntent);

    useEffect(() => {
      if (chatStarted && (marketplaceReturn(window.location.search) || marketplaceIntent)) {
        workbenchStore.openTool('marketplace');
      }
    }, [chatStarted, marketplaceIntent]);

    // The chat-collapse toggle is desktop-only, and a collapsed chat pins .Chat to
    // width:0 (BaseChat.module.scss). Carried into a narrow viewport by a resize,
    // tablet rotate, or Full screen, dismissing the panel would leave a blank screen
    // with no control to restore the chat — so never let it stay collapsed there.
    useEffect(() => {
      if (isSmallViewport && !showChat) {
        chatStore.setKey('showChat', true);
      }
    }, [isSmallViewport, showChat]);

    if (!chatStarted) {
      return null;
    }

    return (
      <div
        className={classNames('ui-root z-workbench h-full overflow-hidden', {
          'flex-shrink-0': isSmallViewport,
          // Shrinkable, so at 1024–1279px (chat pinned at its 380px min) the panel
          // gives up width instead of clipping its header and Publish off the edge.
          'w-[var(--workbench-split-percent)] min-w-0 flex-shrink': !isSmallViewport,
          // Kept mounted (preserves the preview iframe) but collapsed when the user
          // closes it — the chat expands to full width.
          hidden: !showWorkbench,
        })}
      >
        <div
          className={classNames('w-full h-full', {
            // Full-screen editor on narrow viewports. z-30 keeps it above page
            // chrome while staying below modals.
            'fixed inset-0 z-30 bg-page pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]':
              isSmallViewport,
          })}
        >
          <div className="h-full px-2 lg:px-4 py-2">
            <div className="h-full flex flex-col bg-surface border border-hairline rounded-lg overflow-hidden">
              {/* Session timeout warning banner */}
              <ConnectionTimeoutBanner />

              {/* Header: sidebar toggle + tab bar */}
              <div className="flex items-center gap-0.5 pl-1.5 pr-3 py-1.5 border-b border-hairline bg-surface flex-shrink-0">
                {/* Narrow viewports render this panel as a full-screen overlay that
                    covers the header, so the desktop sidebar toggle and the header's
                    Preview/Code/Publish cluster are both unreachable. Without this
                    control there is no way back to the conversation. */}
                {isSmallViewport ? (
                  <button
                    type="button"
                    onClick={() => workbenchStore.hideWorkbench()}
                    title="Back to chat"
                    aria-label="Back to chat"
                    className="flex items-center gap-1 mr-1 flex-shrink-0 rounded-md px-1.5 py-1 text-[12px] font-medium text-secondary hover:text-primary bg-transparent border-0 cursor-pointer touch:min-h-10 touch:px-2.5 touch:text-[13px]"
                  >
                    <span className="i-ph:caret-left text-[13px]" />
                    Chat
                  </button>
                ) : (
                  <button
                    type="button"
                    aria-label={showChat ? 'Hide chat' : 'Show chat'}
                    title={showChat ? 'Hide chat' : 'Show chat'}
                    className={`${showChat ? 'i-ph:sidebar-simple-fill' : 'i-ph:sidebar-simple'} text-base text-secondary hover:text-primary transition-colors mr-1 flex-shrink-0 bg-transparent border-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed`}
                    disabled={!canHideChat}
                    onClick={() => {
                      if (canHideChat) {
                        chatStore.setKey('showChat', !showChat);
                      }
                    }}
                  />
                )}
                <div className="flex-1 min-w-0 overflow-hidden">
                  <PanelTabBar />
                </div>
                {/* Close the preview → full-width chat. Reopen via the top header's
                    Preview / Code / Publish cluster (Header.tsx). */}
                {!isSmallViewport && (
                  <button
                    type="button"
                    onClick={() => workbenchStore.hideWorkbench()}
                    title="Close preview"
                    aria-label="Close preview"
                    className="ui-btn-icon flex-shrink-0 ml-1 text-secondary hover:text-primary"
                  >
                    <span className="i-ph:x text-[15px]" />
                  </button>
                )}
              </div>

              {/* Tab content */}
              <div className="relative flex-1 overflow-hidden">
                {activeTab?.type === 'preview' && <Preview />}
                {activeTab?.type === 'launcher' && <ToolLauncher />}
                {activeTab?.tool === 'deployment' && <DeploymentPanel />}
                {activeTab?.tool === 'forms' && <FormsPanel />}
                {activeTab?.tool === 'integrations' && <IntegrationsPanel />}
                {activeTab?.tool === 'assets' && <AssetsPanel />}
                {activeTab?.tool === 'analytics' && <AnalyticsPanel />}
                {activeTab?.tool === 'payments' && <PaymentsPanel />}
                {activeTab?.tool === 'shop' && <ShopPanel />}
                {activeTab?.tool === 'funnels' && <FunnelsPanel />}
                {activeTab?.tool === 'marketing' && <MarketingPanel />}
                {activeTab?.tool === 'reviews' && <ReviewsPanel />}
                {activeTab?.tool === 'content' && <ContentPanel />}
                {activeTab?.tool === 'automations' && <AutomationsPanel />}
                {activeTab?.tool === 'sections' && <SectionsPanel />}
                {activeTab?.tool === 'marketplace' && <MarketplacePanel />}
                {activeTab?.tool === 'domains' && <DomainsPanel />}
                {activeTab?.tool === 'code' && (codeLocked ? <CodeLockedPanel /> : <CodeViewerPanel />)}
                {activeTab?.tool === 'sub-agent' && <SubAgentPanel />}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
